import { useCallback, useMemo, useState } from 'react';

/**
 *
 * @param {{
 * meta: {
 *  options: {
 *    group: [],
 *    column: [],
 *    fields: [],
 *    filter: [],
 *    order: [],
 *  },
 *  schema: {},
 * },
 * data: {
 *    header: [[]],
 *    body: [[]],
 *    footer: [{}],
 *    columns_cnt: int,
 *    },
 * group_rows: {}
 * }} result
 * @param {boolean} isPortrait,
 */
// eslint-disable-next-line import/prefer-default-export
export const useReportResult = ({ result, isPortrait = true }) => {
  const [closedRowGroups, setClosedRowGroups] = useState([]);
  const header = useMemo(() => result.data.header, [result.data.header]);
  const body = useMemo(() => result.data.body, [result.data.body]);
  const footer = useMemo(() => result.data.footer, [result.data.footer]);
  const groupRows = useMemo(
    () => {
      const gr = result.group_rows;
      // console.log(gr['1']);
      return Object.keys(gr)
        .reduce((RL, level) => gr[level]
          .reduce((R, [start, finish]) => ({
            ...R,
            [start - 1]: { start, finish, level: Number(level) - 1 },
          }), RL), {});
    },
    [result.group_rows],
  );
  const onToggleRowGroup = useCallback(
    (e, group) => setClosedRowGroups(
      (old) => (old.includes(group) ? old.filter((g) => g !== group) : [...old, group]),
    ),
    [],
  );
  const rowLevels = useMemo(
    () => {
      const s = [...new Set(Object.keys(groupRows).map((k) => groupRows[k].level))];
      return [
        ...s.map((level) => ({
          level,
          label: `${level}`,
        })),
        {
          level: Math.max(...s) + 1,
          label: '∞',
        },
      ];
    },
    [groupRows],
  );
  const invisibleRows = useMemo(
    () => closedRowGroups.reduce((R, g) => [
      ...R,
      ...([...Array(groupRows[g].finish - groupRows[g].start + 1)]
        .map((_, i) => groupRows[g].start + i)),
    ], []),
    [closedRowGroups, groupRows],
  );
  const onRowLevelToggle = useCallback(
    (e, level) => {
      const groupsByLevel = Object.keys(groupRows)
        .filter((g) => groupRows[g].level >= level)
        .map((g) => Number(g));
      setClosedRowGroups(groupsByLevel);
    },
    [groupRows],
  );
  // eslint-disable-next-line no-unused-vars
  const gridTemplate = useMemo(
    () => {
      const tableWidth = isPortrait ? 210 * 3.8 : 296 * 3.8;
      const amountColumns = footer[0].filter((c) => c.col_num > 1);
      const maxSymbols = Math.max(...amountColumns.map((c) => c.repr.length));
      const amountColumnNumbers = amountColumns.map((c) => c.col_num);
      const columnCount = header[0].reduce((R, c) => R + c.colspan, 0);
      const columnNumbers = [...Array(columnCount)].map((a, i) => i + 1);
      const groupColumnNumbers = columnNumbers.filter((c) => !amountColumnNumbers.includes(c));
      const groupColumnMaxLength = groupColumnNumbers.reduce((R, c) => ({
        ...R,
        [c]: Math.min(
          Math.max(
            ...body.map((row) => row
              .filter((cell) => cell.col_num === c && cell.colspan === 1)
              .reduce((_, cell) => cell.repr.length, 0)),
          ),
          60,
        ),
      }), {});
      const amountColumnsWidth = amountColumnNumbers.reduce((R, c) => ({
        ...R,
        [c]: Math.max(Math.round(maxSymbols * 7.5), 25),
      }), {});

      const allAmountColumnsWidth = Object.values(amountColumnsWidth).reduce((R, c) => R + c, 0);
      const sumGroupSymbols = Object.values(groupColumnMaxLength).reduce((R, c) => R + c, 0);
      return [...Array(columnCount)].map((a, i) => {
        if (groupColumnNumbers.includes(i + 1)) {
          const w = Math.max(
            100,
            ((tableWidth - allAmountColumnsWidth) * groupColumnMaxLength[i + 1]) / sumGroupSymbols,
          );
          return `${w}px`;
        }
        if (amountColumnNumbers.includes(i + 1)) return `${amountColumnsWidth[i + 1]}px`;
        return '1px';
      });
    },
    [body, footer, header, isPortrait],
  );
  return {
    header,
    body,
    footer,
    groupRows,
    rowLevels,
    closedRowGroups,
    onToggleRowGroup,
    onRowLevelToggle,
    invisibleRows,
    gridTemplate,
  };
};
